import { canPreviewName, resolveExtension } from '../formats/extension-rules';
import type { ExtensionMap, PreviewFormat, SizeUnits, UnknownFileBehavior } from '../models/viewer-types';
import { formatFileSize } from '../utils/file-size';

/**
 * What the tree reads of an entry. The archive hands over more than this — its own flags, offsets and
 * checksums — and all of it travels on untouched.
 */
export interface ArchiveTreeEntry {
  id: number;
  name: string;
  /** Virtual path, when the archive names its entries differently from how it stores them. */
  displayName?: string;
  size: number;
  directory: boolean;
  /** Set by an archive that knows the format of its own entries, such as an MSP360 backup. */
  previewFormat?: PreviewFormat;
  /** Why this entry cannot be opened, as the archive reader saw it. */
  reason?: string;
}

/** What the caller keeps hold of to follow the settings and the selection afterwards. */
export interface ArchiveTreeController {
  setUnknownFileBehavior(value: UnknownFileBehavior): void;
  setSizeUnits(units: SizeUnits): void;
  select(entry?: { id: number } | null): void;
}

interface TreeNode {
  children: Map<string, TreeNode>;
  files: Array<ArchiveTreeEntry>;
}

/**
 * Whether the path is Finder bookkeeping rather than a document anybody meant to store.
 *
 * @param {string} name Path of the entry inside the archive.
 * @return {boolean} True for resource forks and folder metadata.
 */
export function isArchiveMetadata(name: string): boolean {
  return name
    .replaceAll('\\', '/')
    .split('/')
    .some((part) => part.toLowerCase() === '__macosx' || part === '.DS_Store' || part.startsWith('._'));
}

/**
 * Why an entry cannot be previewed.
 *
 * @param {ArchiveTreeEntry} entry The entry to judge.
 * @param {ExtensionMap} [extensionMap] Associations the host application has set.
 * @param {UnknownFileBehavior} [unknownFileBehavior] What to do with a format the viewer does not know.
 * @return {string} The reason, or an empty string when the entry can be opened.
 */
export function archiveEntryReason(
  entry: ArchiveTreeEntry,
  extensionMap: ExtensionMap = {},
  unknownFileBehavior: UnknownFileBehavior = 'reject'
): string {
  if (entry.reason) return entry.reason;

  if (['zip', 'msp360-log-archive'].includes(resolveExtension(entry.name, extensionMap).format)) {
    return 'Nested archives are not supported';
  }

  const openable = ['prompt', 'detect'].includes(unknownFileBehavior) || entry.previewFormat || canPreviewName(entry.name, extensionMap);

  return openable ? '' : 'This file format is not supported by the viewer';
}

// The entries are a flat list of paths; the tree is what those paths spell out together.
function buildTree(entries: Array<ArchiveTreeEntry>): TreeNode {
  const tree: TreeNode = { children: new Map(), files: [] };

  for (const entry of entries) {
    const parts = (entry.displayName || entry.name).split('/').filter(Boolean);
    const folders = entry.directory ? parts : parts.slice(0, -1);
    let node = tree;

    for (const name of folders) {
      if (!node.children.has(name)) node.children.set(name, { children: new Map(), files: [] });

      node = node.children.get(name) as TreeNode;
    }

    if (!entry.directory) node.files.push(entry);
  }

  return tree;
}

/**
 * Draws the catalog of an archive into an element, as folders that open and files that can be picked.
 *
 * @param {HTMLElement} root The element to draw into; whatever it held is replaced.
 * @param {Array<ArchiveTreeEntry>} entries Entries as the archive lists them.
 * @param {Function} open Called with the entry the reader picked.
 * @param {ExtensionMap} [extensionMap] Associations the host application has set.
 * @param {SizeUnits} [sizeUnits] Which unit system the sizes are shown in.
 * @param {UnknownFileBehavior} [unknownFileBehavior] What to do with a format the viewer does not know.
 * @return {ArchiveTreeController} Handles to follow the settings and the selection afterwards.
 */
export function renderArchiveTree(
  root: HTMLElement,
  entries: Array<ArchiveTreeEntry>,
  open: (entry: ArchiveTreeEntry) => void,
  extensionMap: ExtensionMap = {},
  sizeUnits: SizeUnits = 'binary',
  unknownFileBehavior: UnknownFileBehavior = 'reject'
): ArchiveTreeController {
  const document = root.ownerDocument;
  const tree = buildTree(entries);
  const buttons = new Map<number, HTMLAnchorElement>();
  const sizeLabels: Array<[HTMLElement, number]> = [];
  const policyUpdates: Array<() => void> = [];

  const addFile = (list: HTMLUListElement, entry: ArchiveTreeEntry): void => {
    const item = document.createElement('li');
    const button = document.createElement('a');
    const size = document.createElement('span');
    let reason = '';

    const updatePolicy = (): void => {
      reason = archiveEntryReason(entry, extensionMap, unknownFileBehavior);

      if (reason) {
        button.setAttribute('aria-disabled', 'true');
        button.tabIndex = -1;
      } else {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
      }

      button.title = `${entry.name} · ${reason || 'Open preview'}`;
    };

    policyUpdates.push(updatePolicy);
    updatePolicy();

    button.href = '#';
    button.textContent = (entry.displayName || entry.name).split('/').pop() as string;
    button.className = 'archive-file';
    button.setAttribute('part', 'archive-file');
    button.setAttribute('aria-label', `Open ${entry.displayName || entry.name}`);
    button.onclick = (event: MouseEvent): void => {
      event.preventDefault();

      if (!reason) open(entry);
    };

    size.className = 'archive-size';
    size.setAttribute('part', 'archive-file-size');
    size.textContent = ` · ${formatFileSize(entry.size, sizeUnits)}`;
    sizeLabels.push([size, entry.size]);

    item.className = 'archive-item';
    item.append(button, size);
    list.append(item);
    buttons.set(entry.id, button);
  };

  const build = (node: TreeNode): HTMLUListElement => {
    const list = document.createElement('ul');

    list.className = 'archive-list';

    for (const [name, child] of [...node.children].sort((left, right) => left[0].localeCompare(right[0]))) {
      const item = document.createElement('li');
      const folder = document.createElement('details');
      const label = document.createElement('summary');

      item.className = 'archive-item';
      folder.className = 'archive-folder-row';
      label.className = 'archive-folder';
      label.setAttribute('part', 'archive-folder');
      label.textContent = name;
      folder.open = true;
      folder.append(label, build(child));
      item.append(folder);
      list.append(item);
    }

    for (const entry of node.files.sort((left, right) => (left.displayName || left.name).localeCompare(right.displayName || right.name))) {
      addFile(list, entry);
    }

    return list;
  };

  const rootList = build(tree);

  rootList.classList.add('-root');
  root.replaceChildren(rootList);

  if (!entries.length) root.textContent = 'This ZIP archive is empty.';

  return {
    setUnknownFileBehavior(value: UnknownFileBehavior): void {
      unknownFileBehavior = value;

      for (const update of policyUpdates) update();
    },
    setSizeUnits(units: SizeUnits): void {
      for (const [label, bytes] of sizeLabels) label.textContent = ` · ${formatFileSize(bytes, units)}`;
    },
    select(entry?: { id: number } | null): void {
      for (const [id, button] of buttons) {
        if (id === entry?.id) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      }
    }
  };
}
